import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/app/providers";
import {
  deleteNotification,
  fetchNotifications,
  fetchSectionUnreadCounts,
  markAllNotificationsRead,
  markNotificationRead,
  markSectionNotificationsRead,
  NOTIFICATIONS_PAGE_SIZE,
} from "@/features/notifications/lib/notificationsApi";
import type { NotificationAreaCode } from "@/constants/api.notifications";
import {
  emptySectionUnreadCounts,
  notificationMatchesSection,
} from "@/features/notifications/lib/notificationSectionCounts";
import type {
  AppNotification,
  NotificationSectionUnreadCounts,
} from "@/features/notifications/types";
import { WS_EVENTS } from "@/shared/websocket/constants";
import {
  WS_DOM_COLIS,
  WS_DOM_LIST,
  WS_DOM_ORDERS,
  WS_DOM_SALLE,
  WS_DOM_SERVICES,
  WS_DOM_TICKETING,
} from "@/shared/websocket/wsDomEvents";

const NOTIFICATION_REFRESH_WS_EVENTS = [
  "messaging:messageReceive",
  `ws:${WS_EVENTS.MESSAGING_MESSAGE_RECEIVE}`,
  ...WS_DOM_ORDERS,
  ...WS_DOM_TICKETING,
  ...WS_DOM_SERVICES,
  ...WS_DOM_LIST,
  ...WS_DOM_SALLE,
  ...WS_DOM_COLIS,
] as const;

type NotificationsContextValue = {
  items: AppNotification[];
  unreadCount: number;
  sectionCounts: NotificationSectionUnreadCounts;
  loading: boolean;
  hasMore: boolean;
  refresh: (options?: { reset?: boolean }) => Promise<void>;
  markAllRead: () => Promise<void>;
  markRead: (pk: number) => Promise<void>;
  markSectionRead: (section: NotificationAreaCode) => Promise<void>;
  dismiss: (pk: number) => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const WS_REFRESH_DEBOUNCE_MS = 800;

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { token, currentBuilding } = useAuth();
  const buildingId = currentBuilding?.pk ?? null;
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sectionCounts, setSectionCounts] = useState<NotificationSectionUnreadCounts>(
    emptySectionUnreadCounts,
  );
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionCountsRef = useRef(sectionCounts);
  sectionCountsRef.current = sectionCounts;

  const refresh = useCallback(async (_options?: { reset?: boolean }) => {
    if (!token || buildingId == null) {
      setItems([]);
      setUnreadCount(0);
      setSectionCounts(emptySectionUnreadCounts());
      setHasMore(false);
      return;
    }
    setLoading(true);
    try {
      const [result, counts] = await Promise.all([
        fetchNotifications(token, buildingId, {
          limit: NOTIFICATIONS_PAGE_SIZE,
          offset: 0,
        }),
        fetchSectionUnreadCounts(token, buildingId),
      ]);
      setItems(result.items);
      setUnreadCount(result.unreadCount);
      setSectionCounts(counts);
      setHasMore(result.hasMore);
    } catch {
      /* conserve le dernier état connu */
    } finally {
      setLoading(false);
    }
  }, [token, buildingId]);

  const markAllRead = useCallback(async () => {
    if (!token || buildingId == null) {
      return;
    }
    try {
      await markAllNotificationsRead(token, buildingId);
      setItems((current) =>
        current.map((item) => ({
          ...item,
          is_read: true,
          read_at: item.read_at ?? new Date().toISOString(),
        })),
      );
      setUnreadCount(0);
      setSectionCounts(emptySectionUnreadCounts());
    } catch {
      await refresh();
    }
  }, [token, buildingId, refresh]);

  const markRead = useCallback(
    async (pk: number) => {
      if (!token || buildingId == null) {
        return;
      }
      const existing = items.find((item) => item.pk === pk);
      if (existing?.is_read) {
        return;
      }
      try {
        const updated = await markNotificationRead(token, buildingId, pk);
        setItems((current) =>
          current.map((item) => (item.pk === pk ? { ...item, ...updated } : item)),
        );
        setUnreadCount((count) => Math.max(0, count - 1));
        const counts = await fetchSectionUnreadCounts(token, buildingId);
        setSectionCounts(counts);
      } catch {
        await refresh();
      }
    },
    [token, buildingId, items, refresh],
  );

  const markSectionRead = useCallback(
    async (section: NotificationAreaCode) => {
      if (!token || buildingId == null) {
        return;
      }
      if ((sectionCountsRef.current[section] ?? 0) <= 0) {
        return;
      }
      try {
        const updated = await markSectionNotificationsRead(token, buildingId, section);
        if (updated <= 0) {
          return;
        }
        const now = new Date().toISOString();
        setSectionCounts((current) => ({ ...current, [section]: 0 }));
        setUnreadCount((count) => Math.max(0, count - updated));
        setItems((current) =>
          current.map((item) =>
            !item.is_read && notificationMatchesSection(item, section)
              ? { ...item, is_read: true, read_at: item.read_at ?? now }
              : item,
          ),
        );
      } catch {
        await refresh();
      }
    },
    [token, buildingId, refresh],
  );

  const dismiss = useCallback(
    async (pk: number) => {
      if (!token || buildingId == null) {
        return;
      }
      const target = items.find((item) => item.pk === pk);
      try {
        await deleteNotification(token, buildingId, pk);
        setItems((current) => current.filter((item) => item.pk !== pk));
        if (target && !target.is_read) {
          setUnreadCount((count) => Math.max(0, count - 1));
          const counts = await fetchSectionUnreadCounts(token, buildingId);
          setSectionCounts(counts);
        }
      } catch {
        await refresh();
      }
    },
    [token, buildingId, items, refresh],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const scheduleRefresh = () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        void refresh();
      }, WS_REFRESH_DEBOUNCE_MS);
    };

    const onWsEvent = () => {
      scheduleRefresh();
    };

    NOTIFICATION_REFRESH_WS_EVENTS.forEach((name) =>
      window.addEventListener(name, onWsEvent),
    );
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      NOTIFICATION_REFRESH_WS_EVENTS.forEach((name) =>
        window.removeEventListener(name, onWsEvent),
      );
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      items,
      unreadCount,
      sectionCounts,
      loading,
      hasMore,
      refresh,
      markAllRead,
      markRead,
      markSectionRead,
      dismiss,
    }),
    [items, unreadCount, sectionCounts, loading, hasMore, refresh, markAllRead, markRead, markSectionRead, dismiss],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationsProvider");
  }
  return ctx;
}
